import { useQuery, useMutation } from "@tanstack/react-query";
import { useRoute, useLocation } from "wouter";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { ArrowLeft, Star, Save, Loader2, User, Mail, Calendar, CheckCircle, Clock, XCircle, Shield } from "lucide-react";
import { format } from "date-fns";
import type { TestimonialWithUser } from "@shared/schema";

const testimonialEditSchema = z.object({
  rating: z.number().min(1).max(5),
  testimonialText: z.string().min(10, "Testimonial must be at least 10 characters").max(2000, "Testimonial cannot exceed 2000 characters"),
  displayName: z.string().min(1, "Display name is required").max(100),
  photoUrl: z.string().nullable().optional(),
  videoUrl: z.string().nullable().optional(),
  status: z.enum(['pending', 'approved', 'rejected']),
  adminNote: z.string().nullable().optional(),
});

type TestimonialEditFormData = z.infer<typeof testimonialEditSchema>;

export default function AdminTestimonialEditPage() {
  const { toast } = useToast();
  const { isMasterAdmin } = useAuth();
  const [, setLocation] = useLocation();
  const [match, params] = useRoute("/admin/testimonials/:id/edit");
  const testimonialId = params?.id;

  const { data: testimonial, isLoading, error } = useQuery<TestimonialWithUser>({
    queryKey: ['/api/admin/testimonials', testimonialId],
    queryFn: async () => {
      const response = await fetch(`/api/admin/testimonials/${testimonialId}`);
      if (!response.ok) throw new Error('Failed to fetch testimonial');
      return response.json();
    },
    enabled: !!testimonialId,
  });

  const form = useForm<TestimonialEditFormData>({
    resolver: zodResolver(testimonialEditSchema),
    defaultValues: {
      rating: 5,
      testimonialText: "",
      displayName: "",
      photoUrl: null,
      videoUrl: null,
      status: "pending",
      adminNote: null,
    },
    values: testimonial ? {
      rating: testimonial.rating,
      testimonialText: testimonial.testimonialText,
      displayName: testimonial.displayName,
      photoUrl: testimonial.photoUrl,
      videoUrl: testimonial.videoUrl,
      status: testimonial.status as 'pending' | 'approved' | 'rejected',
      adminNote: testimonial.adminNote,
    } : undefined,
  });

  const updateMutation = useMutation({
    mutationFn: async (data: TestimonialEditFormData) => {
      const response = await apiRequest('PATCH', `/api/admin/testimonials/${testimonialId}`, data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/testimonials'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/testimonials', testimonialId] });
      toast({
        title: "Testimonial Updated",
        description: "The testimonial has been successfully updated.",
      });
      setLocation('/admin/testimonials');
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update testimonial",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: TestimonialEditFormData) => {
    updateMutation.mutate(data);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"><CheckCircle className="w-3 h-3 mr-1" /> Approved</Badge>;
      case 'rejected':
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" /> Rejected</Badge>;
      default:
        return <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>;
    }
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-5 h-5 ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
          />
        ))}
      </div>
    );
  };

  if (!match) {
    return <div className="p-8">Invalid route</div>;
  }

  if (!isMasterAdmin) {
    return (
      <div className="w-full max-w-full px-4 md:px-6 py-6 md:py-8">
        <Card>
          <CardContent className="py-12 text-center">
            <Shield className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
            <p className="text-muted-foreground">Only master administrators can access this page.</p>
            <Button className="mt-4" onClick={() => setLocation('/')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="w-full max-w-full px-4 md:px-6 py-6 md:py-8 space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-8 w-64" />
        </div>
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-96" />
          </CardHeader>
          <CardContent className="space-y-6">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-10 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !testimonial) {
    return (
      <div className="w-full max-w-full px-4 md:px-6 py-6 md:py-8">
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">Testimonial not found or you don't have permission to view it.</p>
            <Button className="mt-4" onClick={() => setLocation('/admin/testimonials')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Testimonials
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full max-w-full px-4 md:px-6 py-6 md:py-8 space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setLocation('/admin/testimonials')}
          data-testid="button-back"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Edit Testimonial</h1>
          <p className="text-muted-foreground">Modify testimonial content and status</p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <Card className="md:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg">Submitter Info</CardTitle>
            <CardDescription>Details about who submitted this testimonial</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <Avatar className="h-12 w-12">
                <AvatarImage src={testimonial.user?.profileImageUrl || undefined} />
                <AvatarFallback>
                  {testimonial.user?.firstName?.[0] || testimonial.displayName?.[0] || 'U'}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="font-medium">
                  {testimonial.user?.firstName} {testimonial.user?.lastName}
                </p>
                <p className="text-sm text-muted-foreground">{testimonial.user?.email}</p>
              </div>
            </div>
            
            <Separator />
            
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span>Submitted: {format(new Date(testimonial.createdAt), 'PPP')}</span>
              </div>
              
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">Current Status:</span>
                {getStatusBadge(testimonial.status)}
              </div>
              
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">Terms Accepted:</span>
                <Badge variant={testimonial.termsAccepted ? "default" : "destructive"}>
                  {testimonial.termsAccepted ? "Yes" : "No"}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg">Edit Testimonial</CardTitle>
            <CardDescription>Update the testimonial content and moderation status</CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <FormField
                  control={form.control}
                  name="displayName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Display Name</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="Name to display with testimonial"
                          data-testid="input-display-name"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="rating"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Rating</FormLabel>
                      <div className="flex gap-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            type="button"
                            onClick={() => field.onChange(star)}
                            className="focus:outline-none"
                            data-testid={`button-star-${star}`}
                          >
                            <Star
                              className={`w-8 h-8 transition-colors ${
                                star <= field.value
                                  ? 'fill-yellow-400 text-yellow-400'
                                  : 'text-gray-300 hover:text-yellow-200'
                              }`}
                            />
                          </button>
                        ))}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="testimonialText"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Testimonial Text</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          placeholder="The testimonial content..."
                          className="min-h-[150px]"
                          data-testid="input-testimonial-text"
                        />
                      </FormControl>
                      <FormDescription>
                        {field.value?.length || 0}/2000 characters
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="photoUrl"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Photo URL (Optional)</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            value={field.value || ""}
                            onChange={(e) => field.onChange(e.target.value || null)}
                            placeholder="https://..."
                            data-testid="input-photo-url"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="videoUrl"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Video URL (Optional)</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            value={field.value || ""}
                            onChange={(e) => field.onChange(e.target.value || null)}
                            placeholder="https://youtube.com/..."
                            data-testid="input-video-url"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <Separator />

                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Moderation Status</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger data-testid="select-status">
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="pending">
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-yellow-600" />
                              Pending
                            </div>
                          </SelectItem>
                          <SelectItem value="approved">
                            <div className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-green-600" />
                              Approved
                            </div>
                          </SelectItem>
                          <SelectItem value="rejected">
                            <div className="flex items-center gap-2">
                              <XCircle className="w-4 h-4 text-red-600" />
                              Rejected
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="adminNote"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Admin Note (Optional)</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value || null)}
                          placeholder="Internal note about this testimonial..."
                          className="min-h-[80px]"
                          data-testid="input-admin-note"
                        />
                      </FormControl>
                      <FormDescription>
                        This note is only visible to admins
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex gap-3 pt-4">
                  <Button
                    type="submit"
                    disabled={updateMutation.isPending}
                    data-testid="button-save"
                  >
                    {updateMutation.isPending ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setLocation('/admin/testimonials')}
                    data-testid="button-cancel"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
